import { IODefinition, NoxusReactflowNode } from "@/types/spotflow";
import { useContext, useState, useMemo } from "react";
import {
  Handle,
  Position,
  useNodeId,
  useReactFlow,
  useUpdateNodeInternals,
} from "@xyflow/react";
import { EditorContext } from "../../Editor/EditorContext";
import { useSpotUser } from "@/utils/spotUser";
import {
  removeDynamicInputAtom,
  removeDynamicOutputAtom,
  changeDynamicInputTypeAtom,
  changeDynamicOutputTypeAtom,
  openDropdownMenuAtom,
} from "@/store/editor-jotai";
import { useAtom } from "jotai";
import {
  X,
  MoreVertical,
  Trash2,
  Info,
  CircleDot,
  TriangleAlert,
} from "lucide-react";
import { Badge } from "@/components/shadcn/badge";
import { Button } from "@/components/shadcn/button";
import Image from "next/image";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/shadcn/dropdown-menu";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { UseDisclosureReturn } from "@/utils/useDisclosure";
import { DataType } from "@/generated";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";

const getIconForType: Record<string, string> = {
  str: "react-flow__handle--text",
  text: "react-flow__handle--text",
  dict: "react-flow__handle--file",
  file: "react-flow__handle--file",
  image: "react-flow__handle--image",
  audio: "react-flow__handle--audio",
  number: "react-flow__handle--text",
  bool: "react-flow__handle--text",
  datetime: "react-flow__handle--text",
  basemodel: "react-flow__handle--file",
  object: "react-flow__handle--file",
  list: "react-flow__handle--file",
};

const getIconForDataType = (dataType: string): string => {
  const iconMap: Record<string, string> = {
    str: "/images/svg/abc.svg",
    text: "/images/svg/abc.svg",
    number: "/images/svg/input.svg",
    bool: "/images/svg/check.svg",
    audio: "/images/svg/AudioWaveIcon.svg",
    Audio: "/images/svg/AudioWaveIcon.svg",
    image: "/images/svg/ImageIcon.svg",
    Image: "/images/svg/ImageIcon.svg",
    file: "/images/svg/FileIcon.svg",
    File: "/images/svg/FileIcon.svg",
    dict: "/images/svg/interface.svg",
    list: "/images/svg/page.svg",
    object: "/images/svg/document-trigger-mapping.svg",
    basemodel: "/images/svg/document-trigger-mapping.svg",
    datetime: "/images/svg/input.svg",
  };
  return iconMap[dataType] || "/images/svg/FileIcon.svg";
};

const getLabelForDataType = (dataType: string): string => {
  const labelMap: Record<string, string> = {
    str: "Text",
    number: "Number",
    bool: "Boolean",
    Audio: "Audio",
    Image: "Image",
    File: "File",
    dict: "Object",
    list: "List",
    object: "Object",
    basemodel: "BaseModel",
    datetime: "DateTime",
  };
  return labelMap[dataType] || dataType;
};

const parseTypeValue = (
  value: string
): { data_type: string; is_list: boolean } => {
  const match = value.match(/^list\[(.+)\]$/);
  return match
    ? { data_type: match[1], is_list: true }
    : { data_type: value, is_list: false };
};

const listTypeOptions = (choiceTypes: string[]) =>
  [
    { data_type: "str", label: "List of Text" },
    { data_type: "File", label: "List of Files" },
  ]
    .filter((v) => choiceTypes.includes(v.data_type))
    .map((v) => ({
      value: `list[${v.data_type}]` as DataType,
      label: v.label,
      icon: getIconForDataType(v.data_type),
      iconClassName: v.data_type === "str" ? "filter invert" : undefined,
    }));

const getNameFromDataType = (dataType: string) => {
  if (dataType === "str") return "Text";
  if (dataType === "File") return "File";
  if (dataType === "number") return "Number";
  if (dataType === "bool") return "Boolean";
  if (dataType === "datetime") return "Date Time";
  if (dataType === "basemodel") return "Object";
  if (dataType === "object") return "Object";
  if (dataType === "dict") return "File";
  return dataType;
};

export const getConnectorDataType = (handle: IODefinition, key?: string) => {
  if (handle.type !== "variable_type_size_connector") {
    return handle.definition.data_type;
  }

  const typeDefs = handle.type_definitions;
  if (!key || !typeDefs || !typeDefs[key]) {
    return handle.definition?.data_type ?? "str";
  }

  const data_type = typeDefs[key].data_type;
  switch (data_type) {
    case "dict":
      return "File";
    default:
      return data_type;
  }
};

const getConnectorIsList = (handle: IODefinition, key?: string) => {
  if (handle.type !== "variable_type_size_connector") {
    return handle.definition.is_list;
  }

  const typeDefs = handle.type_definitions;
  if (!key || !typeDefs || !typeDefs[key]) {
    return handle.definition?.is_list ?? false;
  }

  return typeDefs[key].is_list;
};

export const CustomHandle = ({
  handle,
  type,
  isConnected,
  isDeletable = false,
  isTypeEditable = false,
  onChange,
  isVariableHandle,
  isDisabled,
  templateModalControls,
  connector_key,
  nodeType,
}: {
  handle: IODefinition;
  type: "source" | "target";
  isConnected: boolean;
  isDeletable?: boolean;
  isTypeEditable?: boolean;
  onChange?: (value: string) => string | undefined;
  isVariableHandle?: boolean;
  isDisabled?: boolean;
  templateModalControls?: UseDisclosureReturn;
  connector_key?: string;
  nodeType: string;
}) => {
  const { spotGroup } = useSpotUser();
  const acceptedTypes = handle.choices?.map((choice) => choice.data_type);
  const id = isVariableHandle
    ? `${handle.name}::${connector_key}`
    : handle.name;
  const name = isVariableHandle
    ? (connector_key as string)
    : handle.label || handle.name;
  const isEditable = handle.manual;
  const isVariableType = handle.type === "variable_type_connector";
  const { form } = useContext(EditorContext);
  const nodeId = useNodeId() || "";
  const dataType = getConnectorDataType(handle, connector_key);
  const isList = getConnectorIsList(handle, connector_key);

  const flowInstance = useReactFlow();
  const node = flowInstance.getNode(nodeId) as NoxusReactflowNode | undefined;

  const [, removeDynamicOutput] = useAtom(removeDynamicOutputAtom);
  const [, removeDynamicInput] = useAtom(removeDynamicInputAtom);
  const [, changeDynamicInputType] = useAtom(changeDynamicInputTypeAtom);
  const [, changeDynamicOutputType] = useAtom(changeDynamicOutputTypeAtom);
  const [openDropdownMenu, setOpenDropdownMenu] = useAtom(openDropdownMenuAtom);

  const [displayedHandleName, setDisplayedHandleName] = useState(name);

  const { data: typesInfo } = useRemoteData(DATA_KEYS.TYPES_INFORMATION, {});

  const availableTypes = useMemo(() => {
    if (!typesInfo?.data_types) return [];

    return typesInfo.data_types.map((dataType: string) => ({
      value: dataType as DataType,
      label: getLabelForDataType(dataType),
      icon: getIconForDataType(dataType),
      iconClassName: ["str", "number", "bool", "datetime"].includes(dataType)
        ? "filter invert"
        : undefined,
    }));
  }, [typesInfo]);

  const className = `${getIconForType[dataType.toLowerCase()]} ${
    isConnected && "react-flow__handle--connected"
  } ${isList && "react-flow__handle--list"}`;

  const handleRemoveHandle = (e: any) => {
    e.stopPropagation();
    if (isDisabled) {
      templateModalControls?.onOpen();
      return;
    }

    if (type === "source") {
      removeOutput(id);
    } else {
      removeDynamicInput({
        nodeId,
        key: connector_key || "",
        connector_name: handle.name,
      });
      updateNodeInternals(nodeId);
    }
  };

  const handleTypeChange = (newType: string) => {
    if (isDisabled) {
      templateModalControls?.onOpen();
      return;
    }

    const parsed = parseTypeValue(newType);
    if (type === "source") {
      changeDynamicOutputType({
        nodeId,
        keyName: connector_key || "",
        connector_name: handle.name,
        newType: parsed.data_type,
      });
    } else {
      changeDynamicInputType({
        nodeId,
        keyName: connector_key || "",
        connector_name: handle.name,
        newType: parsed.data_type,
        newIsList: parsed.is_list,
      });
    }
    updateNodeInternals(nodeId);
  };

  const handleOnChange = () => {
    if (isDisabled) {
      templateModalControls?.onOpen();
      return;
    }

    if (onChange) {
      const newName = onChange(displayedHandleName);
      if (newName) {
        setDisplayedHandleName(newName);
      }
    }
  };

  const updateNodeInternals = useUpdateNodeInternals();

  const removeOutput = (outputName: string) => {
    removeDynamicOutput({ nodeId, key: outputName, connector_name: "todo" });
    updateNodeInternals(nodeId);
  };

  const menuId = `${nodeId}-${type}-${id}`;
  const isOpen = openDropdownMenu === menuId;

  const handleOpenChange = (open: boolean) => {
    setOpenDropdownMenu(open ? menuId : null);
  };

  if (handle.visible === false) {
    return null;
  }

  const showWarning = name.includes('"');

  return (
    <div
      className={`relative flex items-center w-full ${
        type === "target" ? "justify-start" : "justify-end"
      }`}
    >
      <Handle
        key={id}
        isConnectable
        type={type}
        position={type === "source" ? Position.Right : Position.Left}
        id={id}
        className={className}
        style={{
          right: type === "source" ? "-1rem" : "auto",
          left: type === "target" ? "-1rem" : "auto",
          border: "2px solid",
          borderColor: isConnected ? "#132532" : "#D5D7DA",
          borderRadius: "0.75rem",
          backgroundColor: isConnected ? "#132532" : "#FFFFFF",
          width: "1.5rem",
          height: "1.5rem",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      />

      <div
        className={`flex h-6 items-center gap-1 max-w-full group ${
          type === "source" ? "flex-row-reverse" : "flex-row"
        }`}
      >
        {isEditable ? (
          <input
            type="text"
            value={displayedHandleName}
            onChange={(e) => setDisplayedHandleName(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                (e.target as HTMLInputElement).blur();
              }
            }}
            onBlur={handleOnChange}
            disabled={isDisabled}
            className={`max-w-full text-sm bg-transparent border-none truncate outline-none focus:outline-none p-0 disabled:cursor-not-allowed ${
              type === "source" ? "mr-1" : "ml-1"
            }`}
            style={{
              width: displayedHandleName
                ? `${displayedHandleName.length}ch`
                : "100%",
            }}
          />
        ) : (
          <NoxusTooltip
            isDisabled={
              !(!isVariableType && name.length > 10) &&
              !handle.definition.schema
            }
            content={
              handle.definition.schema ? (
                <div className="flex flex-col gap-2 p-1 min-w-[180px]">
                  <div className="flex items-center justify-between gap-4 border-b border-primary-foreground/20 pb-2">
                    <span className="font-semibold text-sm">
                      {(handle.definition.schema as any)?.title || name}
                    </span>
                    <Badge
                      variant="secondary"
                      className="text-[10px] tracking-wider px-1.5 py-0 h-5"
                    >
                      {dataType === "basemodel" ? "BASEMODEL" : "OBJECT"}
                    </Badge>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {Object.entries(
                      (handle.definition.schema as any)?.properties || {}
                    ).map(([key, value]: [string, any]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between gap-4 text-xs"
                      >
                        <div className="flex items-center gap-1">
                          <span className="font-medium opacity-90">{key}</span>
                          {(
                            (handle.definition.schema as any)?.required || []
                          ).includes(key) && (
                            <span className="text-destructive text-[10px] align-top font-bold">
                              *
                            </span>
                          )}
                        </div>
                        <span className="opacity-70 font-mono text-[10px] bg-primary-foreground/10 px-1 rounded-sm">
                          {value.type}
                        </span>
                      </div>
                    ))}
                    {Object.keys(
                      (handle.definition.schema as any)?.properties || {}
                    ).length === 0 && (
                      <span className="text-xs opacity-70 italic">
                        No properties defined
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                name
              )
            }
            classNameTrigger="max-w-full truncate"
            placement="top"
          >
            <span
              className={`text-sm flex-grow whitespace-nowrap truncate overflow-hidden max-w-full ${
                type === "source" ? "mr-1" : "ml-1"
              }`}
            >
              {isVariableType ? getNameFromDataType(dataType) : name}
            </span>
          </NoxusTooltip>
        )}
        {showWarning && (
          <NoxusTooltip content="Name can't contain quotes. Please update it.">
            <TriangleAlert className="shrink-0 size-3 text-yellow-600" />
          </NoxusTooltip>
        )}
        {handle.description && (
          <NoxusTooltip content={handle.description} placement="top">
            <Info className="size-3 text-muted-foreground cursor-help flex-shrink-0" />
          </NoxusTooltip>
        )}
        {isTypeEditable ? (
          <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="iconsm"
                aria-label="options"
                className="deleteBtn invisible group-hover:visible hover:visible h-auto z-1 py-1"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <MoreVertical className="size-3" strokeWidth={1.25} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="gap-2">
                  <CircleDot className="size-4" />
                  Type
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuRadioGroup
                    value={isList ? `list[${dataType}]` : dataType}
                    onValueChange={handleTypeChange}
                  >
                    {[
                      ...availableTypes.filter(
                        (typeOption) =>
                          acceptedTypes?.includes(
                            typeOption.label as DataType
                          ) ||
                          acceptedTypes?.includes(typeOption.value as DataType)
                      ),
                      ...(type === "target"
                        ? listTypeOptions((acceptedTypes as string[]) ?? [])
                        : []),
                    ].map((typeOption) => (
                      <DropdownMenuRadioItem
                        key={typeOption.value}
                        value={typeOption.value}
                        className="gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Image
                          src={typeOption.icon}
                          alt={typeOption.label}
                          width={16}
                          height={16}
                          className={typeOption.iconClassName}
                        />
                        {typeOption.label}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuItem
                onClick={handleRemoveHandle}
                disabled={
                  !handle.allow_empty && (handle.keys?.length ?? 0) <= 1
                }
                className="text-destructive focus:text-destructive gap-2"
              >
                <Trash2 className="size-4 text-destructive" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          isDeletable && (
            <Button
              variant="ghost"
              size="iconsm"
              aria-label="delete input"
              className="deleteBtn invisible group-hover:visible hover:visible h-auto z-1 py-1"
              onClick={handleRemoveHandle}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <X className="size-3" strokeWidth={1.25} />
            </Button>
          )
        )}
      </div>
    </div>
  );
};
